import { objectSpread2 as _objectSpread2 } from '../_virtual/_rollupPluginBabelHelpers.js';
import { keyCodes } from '../utils/utils.js';
import get from 'lodash/get.js';
import isFunction from 'lodash/isFunction.js';

const verticalKeyToMethod = {
  [keyCodes.up]: 'prev',
  [keyCodes.down]: 'next'
};
const horizontalKeyToMethod = {
  [keyCodes.left]: 'prev',
  [keyCodes.right]: 'next'
};
const bidirectionalKeyToMethod = {
  [keyCodes.left]: 'prev',
  [keyCodes.right]: 'next',
  [keyCodes.up]: 'up',
  [keyCodes.down]: 'down'
};
const homeEndKeyToMethod = {
  [keyCodes.home]: 'first',
  [keyCodes.end]: 'last'
};
const paginationKeyToMethod = {
  [keyCodes.pageUp]: 'prevPage',
  [keyCodes.pageDown]: 'nextPage'
};
const TravelMixin = {
  data() {
    return {
      travel_currentIndex: 0
    };
  },
  computed: {
    travel_config() {
      const travelOptions = get(this.$options, '$travel', null);
      const travelConfig = {
        looped: true,
        orientation: 'horizontal',
        refName: 'items'
      };
      const config = _objectSpread2(_objectSpread2({}, travelConfig), travelOptions);
      return config;
    },
    travel_items() {
      const items = this.$refs[this.travel_config.refName] || [];
      return items;
    },
    travel_length() {
      return this.travel_items.length;
    },
    travel_step() {
      return this.travel_config.grid.cols;
    },
    travel_nextIndex() {
      return this.travel_currentIndex + 1;
    },
    travel_prevIndex() {
      return this.travel_currentIndex - 1;
    },
    travel_aboveIndex() {
      return this.travel_currentIndex - this.travel_step;
    },
    travel_belowIndex() {
      return this.travel_currentIndex + this.travel_step;
    },
    travel_lastIndex() {
      return this.travel_length - 1;
    },
    travel_wrapOffset() {
      return this.travel_step * (this.travel_config.grid.rows - 1);
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.travel_currentIndex = this.travel_findNextInteractive(0, 1);
    });
  },
  beforeDestroy() {
    this.travel_currentIndex = 0;
  },
  methods: {
    bindTravel(event) {
      if (event.ended) {
        return;
      }
      const baseKeyToMethod = Object.assign({}, homeEndKeyToMethod, this.travel_config.hasPagination ? paginationKeyToMethod : {});
      const orientationToKeyToMethod = {
        horizontal: Object.assign({}, baseKeyToMethod, horizontalKeyToMethod),
        vertical: Object.assign({}, baseKeyToMethod, verticalKeyToMethod),
        grid: Object.assign({}, baseKeyToMethod, bidirectionalKeyToMethod)
      };
      const keyToMethod = orientationToKeyToMethod[this.travel_config.orientation];
      const methodName = `travel_${keyToMethod[event.keyCode]}`;
      if (isFunction(this[methodName]) && this.travel_length > 1) {
        this[methodName](event);
      }
      event.ended = true;
    },
    travel_skipNonInteractive(index) {
      const isGrid = this.travel_config.orientation === 'grid';
      switch (index) {
        case this.travel_nextIndex:
          return this.travel_findNextInteractive(index, 1);
        case this.travel_prevIndex:
          return this.travel_findNextInteractive(index, -1);
        case 0:
          if (this.travel_config.looped) {
            return this.travel_findNextInteractive(index, 1);
          }
          return this.travel_currentIndex;
        case isGrid && this.travel_aboveIndex:
          return this.travel_findNextInteractive(index, -this.travel_step);
        case isGrid && this.travel_belowIndex:
          return this.travel_findNextInteractive(index, this.travel_step);
        default:
          return index;
      }
    },
    travel_findNextInteractive(newIndex, movementOffset) {
      const isAtBoundary = newIndex >= this.travel_length || newIndex < 0;
      if (!this.travel_config.looped && isAtBoundary) {
        return this.travel_currentIndex;
      }
      if (newIndex < 0) {
        return this.travel_findNextInteractive(this.travel_length - 1, -1);
      }
      if (this.travel_isInteractive(this.travel_items[newIndex])) {
        return newIndex;
      }
      return this.travel_findNextInteractive(newIndex + movementOffset, movementOffset);
    },
    travel_isInteractive(item) {
      const isDisabled = get(item, 'disabled', false);
      const hasTooltip = get(item, '$attrs.tooltipOptions', false);
      return !isDisabled || isDisabled && hasTooltip;
    },
    travel_setIndex(index) {
      this.travel_currentIndex = this.travel_skipNonInteractive(index);
      const item = this.travel_items[this.travel_currentIndex];
      if (item.$el) {
        item.$el.focus();
      } else {
        item.focus();
      }
    },
    travel_prev(event) {
      event.preventDefault();
      if (this.travel_currentIndex === 0) {
        if (this.travel_config.looped) {
          this.travel_setIndex(this.travel_lastIndex);
          return;
        }
        return;
      }
      this.travel_setIndex(this.travel_prevIndex);
    },
    travel_next(event) {
      event.preventDefault();
      if (this.travel_currentIndex === this.travel_lastIndex) {
        if (this.travel_config.looped) {
          this.travel_setIndex(0);
          return;
        }
        return;
      }
      this.travel_setIndex(this.travel_nextIndex);
    },
    travel_up(event) {
      event.preventDefault();
      const wraparoundIndex = this.travel_currentIndex + this.travel_wrapOffset;
      const topRow = [...Array(this.travel_step).keys()];
      if (topRow.includes(this.travel_currentIndex)) {
        if (this.travel_config.looped) {
          this.travel_setIndex(wraparoundIndex);
          return;
        }
        return;
      }
      this.travel_setIndex(this.travel_aboveIndex);
    },
    travel_down(event) {
      event.preventDefault();
      const wraparoundIndex = this.travel_currentIndex - this.travel_wrapOffset;
      const bottomRow = Array.from({
        length: this.travel_step
      }, (__, index) => index + this.travel_wrapOffset);
      if (bottomRow.includes(this.travel_currentIndex)) {
        if (this.travel_config.looped) {
          this.travel_setIndex(wraparoundIndex);
          return;
        }
        return;
      }
      this.travel_setIndex(this.travel_belowIndex);
    },
    travel_first(event) {
      event.preventDefault();
      this.travel_setIndex(0);
    },
    travel_last(event) {
      event.preventDefault();
      this.travel_setIndex(this.travel_lastIndex);
    },
    travel_prevPage(event) {
      if (this.travel_config.prevPage) {
        this.travel_config.prevPage(event, this.travel_currentIndex);
      }
    },
    travel_nextPage(event) {
      if (this.travel_config.nextPage) {
        this.travel_config.nextPage(event, this.travel_currentIndex);
      }
    }
  }
};
var TravelMixin$1 = TravelMixin;

export { TravelMixin$1 as default };
//# sourceMappingURL=travel.js.map
